import { createStore, applyMiddleware } from 'redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { persistStore, persistReducer } from 'redux-persist';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE } from 'redux-persist/es/constants';
// Import your existing rootReducer from './reducers'
import rootReducer from './reducers';
import { configureStore } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import { SHOW_TOOLBAR } from './actions/types';

// Optimized Logger Class
class OptimizedLogger {
  private dbInstance: IDBDatabase | null = null;
  private logQueue: any[] = [];
  private isProcessing = false;
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly maxLogs = 500;
  private readonly batchSize = 20;
  private readonly flushInterval = 2000; // 2 seconds
  private readonly excludedActions = new Set([
    'TOOLBAR_DISPLAYED', 
    'USER_TYPING', 
    'MOUSE_MOVE',
    FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE,
    "persist/PERSIST", "persist/REHYDRATE"
  ]);

  async initDB(): Promise<IDBDatabase> {
    if (this.dbInstance && this.dbInstance.version > 0) return this.dbInstance;
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('OptimizedLogDB', 2);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains('logs')) {
          const store = db.createObjectStore('logs', { keyPath: 'id', autoIncrement: true });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('type', 'type', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.dbInstance = (event.target as IDBOpenDBRequest).result;
        resolve(this.dbInstance);
      };

      request.onerror = (event) => {
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  async addLog(log: any): Promise<void> {
    if (Platform.OS !== 'web') return;
    
    // Filter out excluded actions
    if (this.excludedActions.has(log.action) || this.excludedActions.has(log.type)) {
      return;
    }

    // Add to batch queue
    this.logQueue.push({
      ...log,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString()
    });

    // Schedule batch processing
    this.scheduleBatchProcess();

    // Track user interaction in context tracker
    try {
      const { addContextEntry } = await import('@services/contextTracker');
      const logContent = JSON.stringify({
        action: log.action || log.type,
        payload: log.payload,
        url: log.url,
        method: log.method
      });
      await addContextEntry('user_interaction', logContent, {
        action: log.action || log.type,
        timestamp: new Date().toISOString()
      });

      // Analyze interaction for TensorFlow proactive AI
      // if (log.action || log.type) {
      //   const { analyzeInteraction } = await import('@services/tensorflowProactiveAI');
      //   analyzeInteraction(log.action || log.type, logContent).catch(() => {
      //     // Silently fail - proactive AI is optional
      //   });
      // }
    } catch (error) {
      // Silently fail - context tracking is optional
      if (__DEV__) {
        console.warn('Failed to track user interaction in context:', error);
      }
    }
  }

  private scheduleBatchProcess(): void {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.processBatch();
    }, this.flushInterval);

    // Force process if queue is full
    if (this.logQueue.length >= this.batchSize) {
      this.processBatch();
    }
  }

  private async processBatch(): Promise<void> {
    if (this.isProcessing || this.logQueue.length === 0) return;
    
    this.isProcessing = true;
    
    try {
      const db = await this.initDB();
      const transaction = db.transaction('logs', 'readwrite');
      const store = transaction.objectStore('logs');
      
      // Process batch
      const batch = this.logQueue.splice(0, this.batchSize);
      
      // Add all logs in the batch
      const promises = batch.map(log => 
        new Promise<void>((resolve, reject) => {
          const request = store.add(log);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        })
      );

      await Promise.all(promises);
      
      // Clean up old logs periodically
      await this.cleanupOldLogs(store);
      
    } catch (error) {
      console.error('Failed to process log batch:', error);
      // Return logs to queue if processing failed
      this.logQueue.unshift(...this.logQueue.splice(-this.batchSize));
    } finally {
      this.isProcessing = false;
    }
  }

  private async cleanupOldLogs(store: IDBObjectStore): Promise<void> {
    // Only cleanup every 10th batch to reduce overhead
    if (Math.random() > 0.1) return;

    const countRequest = store.count();
    countRequest.onsuccess = () => {
      if (countRequest.result > this.maxLogs) {
        const index = store.index('timestamp');
        const cursorRequest = index.openCursor();
        let deleteCount = countRequest.result - this.maxLogs;
        
        cursorRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor && deleteCount > 0) {
            store.delete(cursor.primaryKey);
            deleteCount--;
            cursor.continue();
          }
        };
      }
    };
  }

  async fetchLogs(): Promise<any[]> {
    if (Platform.OS !== 'web') return [];
    
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction('logs', 'readonly');
        const store = transaction.objectStore('logs');
        const index = store.index('timestamp');
        const request = index.getAll();
        
        request.onsuccess = () => resolve(request.result.reverse()); // Most recent first
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      return [];
    }
  }

  async clearLogs(): Promise<void> {
    if (Platform.OS !== 'web') return;
    
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction('logs', 'readwrite');
        const store = transaction.objectStore('logs');
        const request = store.clear();
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  }
}

// Create singleton instance
const optimizedLogger = new OptimizedLogger();

// Backward compatibility exports
export const saveLog = async (log: any) => optimizedLogger.addLog(log);
export const fetchLogs = async () => optimizedLogger.fetchLogs();
export const clearDB = async () => optimizedLogger.clearLogs();

// Optimized API Logger Middleware
const optimizedApiLogger = (store: any) => (next: any) => (action: any) => {
  // Handle Redux persist actions
  if (action.type === FLUSH || action.type === PAUSE || action.type === PERSIST || 
      action.type === PURGE || action.type === REGISTER || action.type === REHYDRATE || 
      action.type === "persist/PERSIST" || action.type === "persist/REHYDRATE") {
    return next(action);
  }

  if (typeof action === 'function') {
    const log = {
      type: 'API_CALL',
      action: action.name,
      duration: undefined,
      response: undefined,
      error: undefined,
      timestamp: new Date().toISOString(),
    };

    const startTime = Date.now();

    const result = action(store.dispatch, store.getState);
    
    if (result && typeof result.then === 'function') {
      result
        .then((response: any) => {
          if (Platform.OS === 'web') {
            log.response = response;
            log.duration = Date.now() - startTime;
            if (log.action !== 'TOOLBAR_DISPLAYED') {
              optimizedLogger.addLog(log);
            }
          }
        })
        .catch((error: any) => {
          if (Platform.OS === 'web') {
            log.error = error;
            log.duration = Date.now() - startTime;
            optimizedLogger.addLog(log);
          }
        });
    }

    return next(action);
  } else {
    try {
      const log = {
        type: 'ACTION',
        action: action.type,
        payload: action.payload,
        timestamp: new Date().toISOString(),
        duration: 0,
      };

      const startTime = Date.now();
      const result = next(action);
      log.duration = Date.now() - startTime;
      
      optimizedLogger.addLog(log);
      return result;
    } catch (e) {
      console.error('API Logger error:', e);
      return next(action);
    }
  }
};

// Define your middleware
const middlewares = Platform.OS !== 'web' ? [] : [optimizedApiLogger];

// Import persist config from separate file to avoid circular dependency
import { persistConfig } from './persistConfig';

const persistedReducer = persistReducer(persistConfig, rootReducer);
const initialState = rootReducer(undefined, {type: 'INITIAL_STATE'})

export type DefaultRootState = ReturnType<typeof store.getState>

// Create the Redux store
const store = configureStore({
  reducer: persistedReducer,
  preloadedState: initialState,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, SHOW_TOOLBAR, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE],
      ignoredPaths: ['modal', 'actionMaker', 'lastAction', 'uploadPreview', 'calendar', 'notifications', 'toolbar', 'modals', 'report'],
    }
  }).concat(middlewares),
  devTools: __DEV__ ? true : false
});

// Persistor for use with Redux Persist
const persistor = persistStore(store);

export { store, persistor };
